import FleetModal, { useAsyncModal } from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { UploadOutlined } from '@ant-design/icons';
import rule from '@/libs/antd/rules';
import {
	CustomerLegalDocument,
	FileData,
} from '@/libs/fleet-api/services/types';
import {
	DatePicker,
	Divider,
	Form,
	Input,
	Row,
	Col,
	Alert,
	UploadFile,
	Upload,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import dayjs from 'dayjs';
import { FC } from 'react';
import { useMutation } from '@tanstack/react-query';
import { uploader } from '@/libs/fleet-api/uploader';
import { useUserData } from '@/libs/fleet-api/hooks';

type LegalDocumentForm = CustomerLegalDocument;

export const useLegalDocumentModal = () => {
	return useAsyncModal<LegalDocumentForm, LegalDocumentForm>();
};

const LegalDocumentModal: FC<
	ReturnType<typeof useLegalDocumentModal> & {
		customerId: string;
		prospectId: string;
	}
> = (props) => {
	const { groupName } = useUserData() ?? {};
	const { form, value, fieldName } = useForm<LegalDocumentForm>();
	const uploadMutation = useMutation({
		mutationFn: uploader,
	});
	const documentMandatory =
		groupName === 'CCA'
			? value?.mandatoryOnCcaNap
			: groupName === 'FRM'
				? value?.mandatoryOnFrmNap
				: false;

	return (
		<FleetModal
			{...props.modal}
			width={940}
			closable={false}
			buttonPosition="end"
			okText="SAVE"
			cancelText="BACK"
			onOk={async () => {
				const values = await form.validateFields();
				props.close(values);
			}}
			afterOpenChange={(isOpen) => {
				if (!isOpen) {
					form.resetFields();
				} else if (props.data) {
					form.setFieldsValue(props.data);
				}
			}}
		>
			<FleetTitle>Edit Dokumen</FleetTitle>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				style={{ marginTop: 20 }}
				labelCol={{ span: 5 }}
				wrapperCol={{ span: 19 }}
			>
				<Form.Item
					name={fieldName('id')}
					noStyle
				/>
				<Form.Item
					name={fieldName('file')}
					noStyle
				/>
				<Form.Item
					name={fieldName('boardOfDirectorsAuthority')}
					noStyle
				/>
				<Form.Item
					name={fieldName('clause')}
					noStyle
				/>
				<Form.Item
					name={fieldName('documentDate')}
					noStyle
				/>
				<Form.Item
					name={fieldName('isActive')}
					noStyle
				/>
				<Form.Item
					name={fieldName('mandatoryOnCcaNap')}
					noStyle
				/>
				<Form.Item
					name={fieldName('mandatoryOnFrmNap')}
					noStyle
				/>
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('documentType')}
							label="Jenis Dokumen"
						>
							<TextArea
								rows={3}
								disabled
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('documentNo')}
							label="No. Dokumen"
							rules={[rule.required()]}
						>
							<Input />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('documentDate')}
							label="Tanggal Dokumen"
							getValueProps={(value) => ({
								value: value && dayjs(value),
							})}
							normalize={(value) =>
								value && dayjs(value).utc(true).toISOString()
							}
							rules={[rule.required()]}
						>
							<DatePicker
								disabledDate={(now) => now > dayjs().subtract(1, 'day')}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('file')}
							label="Upload Dokumen"
							rules={documentMandatory ? [rule.required()] : []}
							labelCol={{ span: 8 }}
							style={{ verticalAlign: 'top' }}
							getValueProps={(value) => {
								const file = value as FileData;
								return {
									fileList: file?.id
										? ([
												{
													uid: file.id,
													name: file.name,
													thumbUrl: file.url,
												},
											] as UploadFile[])
										: [],
								};
							}}
							normalize={({ file }: { file: UploadFile }) => {
								return {
									id: file.uid,
									format: file.type,
									name: file.name,
									url: file.thumbUrl,
								} as FileData;
							}}
						>
							<Upload.Dragger
								listType="picture"
								accept=".jpg,.jpeg,.png"
								maxCount={1}
								beforeUpload={() => false}
								onRemove={() => {
									form.setFieldValue(fieldName('file'), null);
									return false;
								}}
								onChange={async ({ file }) => {
									const fileid = Date.now().toString();
									const filepath = `customer/${props.customerId}/${props.prospectId}/nap/documents`;

									try {
										const url = await uploadMutation.mutateAsync({
											file: file as unknown as File,
											fileid,
											filepath,
										});
										form.setFieldValue(fieldName('file'), {
											format: 'jpeg',
											id: fileid,
											name: file.name,
											path: filepath,
											url,
										});
									} catch {
										form.setFieldValue(fieldName('file'), null);
									}
								}}
							>
								<UploadOutlined
									style={{
										fontSize: 25,
										color: '#BABABA',
										display: 'block',
									}}
								/>
							</Upload.Dragger>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Alert
					message={
						<>
							Kolom Keterangan dapat diisi sebagai berikut:
							<ul style={{ marginTop: 0, marginBottom: 0, paddingLeft: 15 }}>
								<li>
									Yang mewakili: [berdasarkan Pasal Tugas dan Wewenang Direksi]
								</li>
								<li>
									Persetujuan Kepada Direksi dalam menandatangani Perjanjian
									Pembiayaan: [berdasarkan Pasal Tugas dan Wewenang Direksi
									harus dengan persetujuan Dekom/RUPS]
								</li>
							</ul>
						</>
					}
					type="info"
					showIcon
					style={{ marginTop: 30 }}
				/>
				<Form.Item
					name={fieldName('notes')}
					label="Keterangan"
					style={{ marginTop: 20 }}
					rules={
						form?.getFieldValue(fieldName(['isNoteMandatory']))
							? [rule.required()]
							: []
					}
				>
					<TextArea rows={3} />
				</Form.Item>
			</Form>
		</FleetModal>
	);
};

export default LegalDocumentModal;
